import { useContext, useEffect, useState } from "react";
import {
  DestinationStep,
  STEP_CONFIRM_TRANSFER_DETAILS,
  STEP_DESTINATION_ACCOUNT,
  STEP_DESTINATION_CONNECT_ES,
  STEP_DESTINATION_CONNECT_ES_DETAILS,
  STEP_DESTINATION_CONNECT_ES_UPLOAD_STATEMENT,
  STEP_DESTINATION_CONNECT_IRA,
  STEP_DESTINATION_FLASH_LOADING_TRANFER_IN,
  STEP_DESTINATION_HELP,
  STEP_DESTINATION_HELP_RESULT,
  STEP_DESTINATION_IRA_FOUND,
  STEP_DESTINATION_OPEN_AND_CONNECT_IRA,
  STEP_DESTINATION_OPTIONS,
} from "../../class/transferDetails";
import Analytics from "../../utils/productAnalytics";
import { TransferContext } from "../../context/TransferContext";
import { TransferIntent } from "../../class/transferIntent";
import DestinationAccount, { AccountType } from "../../class/destinationAccount";
import { AppContext } from "../../context/AppContext";
import { BRIDGE_DEMO, TRANSFER_FLOW_IN, TRANSFER_FLOW_IN_WITH_SOURCE_ACCS } from "../../typings";
import { type UploadedFile } from "@manifesthhqinc/ui-library";
import manualWait from "../../utils/manualWait";
import { FileUploadStatus, UPLOAD_SUCCESS } from "../../class/file";

const getPlanNameFromTransferIntent = (data: TransferIntent | undefined): string => {
  if (!data) return "";
  if (!data.isEmployerSponsored) return "Individual Retirement Account";
  return data.transferAccount?.planName || "Employer-sponsored plan";
};

// const getPlanSponsorFromTransferIntent = (data: TransferIntent | undefined): string => {
//   if (!data || !data.transferAccount) return "";
//   if (!data.isEmployerSponsored) return "";
//   return data.transferAccount.planSponsor || "Old employer";
// };

export type DestinationAccountControllerType = ReturnType<typeof useDestinationAccount>;

const useDestinationAccount = () => {
  const { transferFlow, appEnv } = useContext(AppContext);
  const {
    goToScreen,
    transferIntentDetails,
    isLoading,
    createDestainationInAPI,
    transferDetails,
    setIsLoading,
    getEmployerOptions,
    providerList,
    currentScreen,
    uploadDestiationFile,
    getUserTransferOptions,
    transferOptions,
    deleteCurrentDestination,
    deleteDestinationAccountStatement,
    setIsDestinationAnimationLoaded,
    isIRAFoundAnimationLoaded,
    setIsIRAFoundAnimationLoaded,
  } = useContext(TransferContext);

  const [currentDestinationDetails, setCurrentDestinationDetails] = useState<DestinationAccount>(
    new DestinationAccount(),
  );
  const [isEmployerSponsored, setIsEmployerSponsored] = useState<boolean>(
    transferIntentDetails?.isEmployerSponsored || false,
  );
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [confirmedTradIRANumber, setConfirmedTradIRANumber] = useState<string>("");
  const [confirmedRothIRANumber, setConfirmedRothIRANumber] = useState<string>("");
  const [formValidation, setFormValidation] = useState<Record<string, string>>({});
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [lockProvider, setLockProvider] = useState<boolean>(false);

  const validateFormDetails = (details: Partial<DestinationAccount>) => {
    let isValidated = true;
    let validation = {};
    if (currentScreen === STEP_DESTINATION_CONNECT_IRA) {
      const iraValidation = {
        providerName: !details.providerName ? "Please provide account name" : "",
        confirmedTradIRANumber:
          details.traditionalIraAccountNumber !== confirmedTradIRANumber
            ? "Please confirm your traditional IRA account number"
            : "",
        confirmedRothIRANumber:
          details.rothIraAccountNumber !== confirmedRothIRANumber ? "Please confirm your roth IRA account number" : "",
        accountNumber:
          !details.traditionalIraAccountNumber && !details.rothIraAccountNumber
            ? "Please provide either Traditional IRA or Roth IRA number"
            : "",
      };
      isValidated = Object.values(iraValidation).filter((e) => e).length === 0;
      validation = iraValidation;
    }
    if (currentScreen === STEP_DESTINATION_CONNECT_ES) {
      isValidated = details.planSponsor !== "";
      validation = {
        planSponsor: details.planSponsor === "" ? "Please provide your current employer name" : "",
      };
    }
    if (currentScreen === STEP_DESTINATION_CONNECT_ES_UPLOAD_STATEMENT) {
      isValidated = details?.destinationStatementFileNames?.length
        ? details?.destinationStatementFileNames?.length > 0
        : false;
      validation = {
        destinationStatementFileNames:
          details?.destinationStatementFileNames?.length && details?.destinationStatementFileNames?.length > 0
            ? ""
            : "Please upload a statement to continue",
      };
    }
    if (currentScreen === STEP_DESTINATION_CONNECT_ES_DETAILS) {
      validation = {
        providerName: !details.providerName ? "Please provide the account provider name" : "",
      };
      isValidated = Object.values(validation).filter((e) => e).length === 0;
    }

    return { isValidated, validation };
  };

  useEffect(() => {
    setFormValidation({});
  }, [currentDestinationDetails, confirmedRothIRANumber, confirmedTradIRANumber]);

  const onClickContinue = async () => {
    // Validate form details
    const { isValidated, validation } = validateFormDetails(currentDestinationDetails);
    if (!isValidated) {
      setFormValidation(validation);
      return;
    }
    if (currentScreen === STEP_DESTINATION_OPEN_AND_CONNECT_IRA) {
      setLockProvider(true);
      goToScreen(STEP_DESTINATION_CONNECT_IRA);
    }
    if (currentScreen === STEP_DESTINATION_FLASH_LOADING_TRANFER_IN) {
      goToScreen(STEP_DESTINATION_ACCOUNT);
    }
    if (currentScreen === STEP_DESTINATION_ACCOUNT || currentScreen === STEP_DESTINATION_IRA_FOUND) {
      Analytics.track({ name: "Confirmed destination account" });
      if (transferDetails.destinationAccount === null) {
        if (!transferIntentDetails) return;
        if (transferFlow === TRANSFER_FLOW_IN || transferFlow === TRANSFER_FLOW_IN_WITH_SOURCE_ACCS) {
          await createDestainationInAPI();
        } else {
          await createDestainationInAPI(currentDestinationDetails);
        }
      }

      goToScreen(STEP_CONFIRM_TRANSFER_DETAILS);
    }
    if (
      currentScreen === STEP_DESTINATION_CONNECT_IRA ||
      currentScreen === STEP_DESTINATION_CONNECT_ES_UPLOAD_STATEMENT ||
      currentScreen === STEP_DESTINATION_CONNECT_ES_DETAILS
    ) {
      setIsEmployerSponsored(currentDestinationDetails.destinationAccountType === AccountType.employerSponsored);
      await createDestainationInAPI(currentDestinationDetails);
      goToScreen(STEP_DESTINATION_ACCOUNT);
    }

    if (currentScreen === STEP_DESTINATION_CONNECT_ES) {
      goToScreen(STEP_DESTINATION_CONNECT_ES_UPLOAD_STATEMENT);
      return;
    }
  };

  useEffect(() => {
    if (currentScreen === STEP_DESTINATION_OPEN_AND_CONNECT_IRA) {
      setCurrentDestinationDetails(transferDetails.destinationAccount || new DestinationAccount());
    } else if (currentScreen === STEP_DESTINATION_ACCOUNT && transferDetails.destinationAccount) {
      setIsEmployerSponsored(
        transferDetails.destinationAccount.destinationAccountType === AccountType.employerSponsored,
      );
      setCurrentDestinationDetails(transferDetails.destinationAccount);
    } else if (
      currentScreen === STEP_DESTINATION_ACCOUNT &&
      (transferFlow === TRANSFER_FLOW_IN || transferFlow === TRANSFER_FLOW_IN_WITH_SOURCE_ACCS)
    ) {
      setCurrentDestinationDetails(
        new DestinationAccount({
          providerName: transferIntentDetails?.transferAccount?.providerName,
          planName: getPlanNameFromTransferIntent(transferIntentDetails),
          planSponsor: getPlanNameFromTransferIntent(transferIntentDetails),
          destinationAccountType: transferIntentDetails?.isEmployerSponsored
            ? AccountType.employerSponsored
            : AccountType.ira,
        }),
      );
    } else if (currentScreen === STEP_DESTINATION_IRA_FOUND) {
      setCurrentDestinationDetails(
        new DestinationAccount({
          destinationAccountType: AccountType.ira,
          providerName: transferIntentDetails?.accounts[0]?.providerName || "",
          traditionalIraAccountNumber: transferIntentDetails?.accounts[0]?.accountNumber,
          statementSkipped: "TRUE",
        }),
      );
    } else if (currentScreen === STEP_DESTINATION_CONNECT_ES_UPLOAD_STATEMENT) {
      setUploadedFiles(
        currentDestinationDetails.destinationStatementFileNames.map((fileName, index) => ({
          file: new File([], fileName),
          id: `${fileName}-${index}`,
          fileName,
          status: "completed" as UploadedFile["status"],
          progress: 100,
          fileSize: "",
        })),
      );
    }
  }, [currentScreen]);

  const uploadFileToAPI = async (uploadingFile: UploadedFile): Promise<FileUploadStatus> => {
    const progressInterval = setInterval(() => {
      setUploadedFiles((prev) =>
        prev.map((file) => {
          if (uploadingFile && file.status === "uploading" && file.progress < 80) {
            return { ...file, progress: Math.min(file.progress + 10, 80) };
          }
          return file;
        }),
      );
    }, 200);
    if (appEnv === BRIDGE_DEMO) {
      await manualWait(3000);

      setUploadedFiles((prev) =>
        prev.map((uploadedFie) =>
          uploadedFie.id === uploadingFile.id
            ? { ...uploadingFile, status: "completed" as const, progress: 100 }
            : uploadingFile,
        ),
      );
      clearInterval(progressInterval);
      const updatedDestinationDetails = new DestinationAccount({
        ...currentDestinationDetails,
        statementSkipped: "FALSE",
        destinationStatementFileNames: [
          ...currentDestinationDetails.destinationStatementFileNames,
          uploadingFile.fileName,
        ],
      });
      setCurrentDestinationDetails(updatedDestinationDetails);
      setIsUploading(false);
      return UPLOAD_SUCCESS;
    } else {
      const status = await uploadDestiationFile(uploadingFile);
      if (status === UPLOAD_SUCCESS) {
        setUploadedFiles((prev) =>
          prev.map((uploadedFie) =>
            uploadedFie.id === uploadingFile.id
              ? { ...uploadingFile, status: "completed" as const, progress: 100 }
              : uploadingFile,
          ),
        );
        const updatedDestinationDetails = new DestinationAccount({
          ...currentDestinationDetails,
          statementSkipped: "FALSE",
          destinationStatementFileNames: [
            ...currentDestinationDetails.destinationStatementFileNames,
            uploadingFile.fileName,
          ],
        });
        setCurrentDestinationDetails(updatedDestinationDetails);
      } else {
        // TODO: do something when
        setUploadedFiles((prev) => prev.filter((uploadedFie) => uploadedFie.id === uploadingFile.id));
      }
      setIsUploading(false);
      clearInterval(progressInterval);
      return status;
    }
  };

  const handleUploadFile = async (files: UploadedFile[]): Promise<FileUploadStatus> => {
    setIsUploading(true);
    const uploadingFile = {
      file: files[0].file,
      fileName: files[0].fileName,
      id: `${files[0].fileName}-${Date.now()}`,
      status: "uploading" as const,
      progress: 0,
      fileSize: files[0].fileSize,
    };

    setUploadedFiles([...uploadedFiles, uploadingFile]);
    return await uploadFileToAPI(uploadingFile);
  };

  const handleRemoveFile = async (fileName: string) => {
    const updatedDestinationDetails = new DestinationAccount({
      ...currentDestinationDetails,
      destinationStatementFileNames: currentDestinationDetails.destinationStatementFileNames.filter(
        (e) => e === fileName,
      ),
      statementSkipped: "TRUE",
    });
    if (appEnv !== BRIDGE_DEMO) {
      // REMOVE IN API
      await Promise.all([
        deleteDestinationAccountStatement(fileName),
        // createDestainationInAPI(updatedDestinationDetails),
      ]);
    }
    setCurrentDestinationDetails(updatedDestinationDetails);
    setUploadedFiles(uploadedFiles.filter((e) => e.fileName === fileName));
    setIsUploading(false);
  };

  const onClickSkip = () => {
    setCurrentDestinationDetails(new DestinationAccount());
    goToScreen(STEP_DESTINATION_OPTIONS);
  };

  const updateDestinationDetails = (updatedData: Partial<DestinationAccount>) => {
    setCurrentDestinationDetails({ ...currentDestinationDetails, ...updatedData });
  };

  const onClickSkipStatement = () => {
    goToScreen(STEP_DESTINATION_CONNECT_ES_DETAILS);
  };

  const onClickExploreMyOptions = async () => {
    Analytics.track({ name: "Clicked Explore my options" });
    if (appEnv === BRIDGE_DEMO) {
      goToScreen(STEP_DESTINATION_HELP);
    } else {
      const transferOptionsRes = await getUserTransferOptions();
      if (!transferOptionsRes) {
        goToScreen(STEP_DESTINATION_HELP);
      } else {
        goToScreen(STEP_DESTINATION_HELP_RESULT);
      }
    }
  };

  const onClickDifferentDestination = async () => {
    // step 1: delete current destination
    await deleteCurrentDestination(currentDestinationDetails.destinationAccountId);
    // take back to explore options
    if (transferOptions) {
      goToScreen(STEP_DESTINATION_HELP_RESULT);
    } else {
      goToScreen(STEP_DESTINATION_OPTIONS);
    }
  };

  return {
    isLoading,
    onClickContinue,
    currentDestinationDetails,
    currentDestinationStep: currentScreen,
    isEmployerSponsored,
    setIsLoading,
    providerIRAName: transferIntentDetails?.accounts[0]?.providerName,
    onClickSkip,
    goToDestinationScreen: (screen: DestinationStep) => goToScreen(screen),
    updateDestinationDetails,
    getEmployerOptions,
    onClickSkipStatement,
    providerList,
    transferFlow,
    setIsEmployerSponsored,
    handleUploadFile,
    uploadedFiles,
    goToScreen,
    formValidation,
    confirmedTradIRANumber,
    setConfirmedTradIRANumber,
    confirmedRothIRANumber,
    setConfirmedRothIRANumber,
    isUploading,
    onClickExploreMyOptions,
    onClickDifferentDestination,
    lockProvider,
    handleRemoveFile,
    setIsDestinationAnimationLoaded,
    isIRAFoundAnimationLoaded,
    setIsIRAFoundAnimationLoaded,
  };
};

export default useDestinationAccount;
